import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Stack, Select, MenuItem, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Skeleton, IconButton, Tooltip,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import * as XLSX from 'xlsx';
import { analyticsApi } from '../services/api';
import { ErrorState } from './common/States';
import EmptyState from './common/EmptyState';
import SubTabs from './common/SubTabs';
import ExportGate from './common/ExportGate';
import { addDays, apiError, formatDate, formatMoney as formatMoneyBase, formatNumber, mskToday } from '../utils/format';
import { mpPath } from '../utils/marketplaces';

const formatMoney = (v, opts) => formatMoneyBase(v, { kopecks: true, ...opts });

const MONTHS = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];
const monthLabel = (m) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;

// The last day of the month that starts at m (YYYY-MM-01).
const lastDay = (m) => {
  const y = Number(m.slice(0, 4));
  const mo = Number(m.slice(5, 7));
  const next = mo === 12 ? `${y + 1}-01-01` : `${y}-${String(mo + 1).padStart(2, '0')}-01`;
  return addDays(next, -1);
};

const REAL_COLS = [
  { key: 'sold', label: 'Продано, шт.', num: true },
  { key: 'returned', label: 'Возвращено, шт.', num: true },
  { key: 'revenue', label: 'Реализовано на сумму' },
  { key: 'standard_fee', label: 'Вознаграждение Ozon' },
  { key: 'loyalty', label: 'Выплаты по лояльности' },
  { key: 'total', label: 'Итого к начислению' },
];

const CASH_COLS = [
  { key: 'orders', label: 'Заказы' },
  { key: 'returns', label: 'Возвраты' },
  { key: 'commission', label: 'Комиссия' },
  { key: 'delivery_and_returns', label: 'Логистика' },
  { key: 'services', label: 'Услуги' },
  { key: 'invoice_transfer', label: 'К выплате' },
];

const saveSheet = (rows, sheet, file) => {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), sheet);
  XLSX.writeFile(wb, file);
};

// Ozon reports loaded by the sync: monthly realization reports and the half-month financial report.
const OzonReports = ({ accounts }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('realization');
  const [accountId, setAccountId] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const name = (id) => accounts.find((a) => a.id === id)?.name || '';
  const many = accounts.length > 1 && !accountId;

  const load = useCallback(async () => {
    setError('');
    setData(null);
    try {
      const { data: d } = await analyticsApi.ozonReports(accountId ? { account_id: accountId } : {});
      setData(d);
    } catch (e) {
      setError(apiError(e));
    }
  }, [accountId]);
  useEffect(() => { load(); }, [load]);

  const downloadReport = async (r) => {
    try {
      const { data: rows } = await analyticsApi.ozonRealizationRows({ account_id: r.account_id, month: r.month });
      saveSheet([
        ['№', 'SKU', 'Артикул', 'Товар', 'Цена продавца', 'Доставлено, шт.', 'Реализовано на сумму', 'Вознаграждение Ozon', 'Выплаты по лояльности',
          'Итого к начислению', 'Возвращено, шт.', 'Возвраты на сумму', 'Вознаграждение по возвратам', 'Лояльность по возвратам', 'Итого по возвратам', 'Доля комиссии'],
        ...rows.map((x) => [x.row_number, x.sku, x.offer_id, x.name, Number(x.seller_price), x.sale_qty, Number(x.sale_amount), Number(x.sale_standard_fee),
          Number(x.sale_loyalty), Number(x.sale_total), x.return_qty, Number(x.return_amount), Number(x.return_standard_fee), Number(x.return_loyalty),
          Number(x.return_total), Number(x.commission_ratio)]),
      ], 'Отчёт о реализации', `marqit_ozon_реализация_${r.month.slice(0, 7)}${many ? `_${name(r.account_id)}` : ''}.xlsx`);
    } catch (e) {
      setError(apiError(e));
    }
  };

  const exportCash = () => saveSheet([
    ['Период с', 'по', ...(many ? ['Кабинет'] : []), ...CASH_COLS.map((c) => c.label)],
    ...data.cash_flows.map((c) => [c.date_from, c.date_to, ...(many ? [name(c.account_id)] : []), ...CASH_COLS.map((col) => (c[col.key] == null ? '' : Number(c[col.key])))]),
  ], 'Финансовый отчёт', `marqit_ozon_финансовый_отчёт_${mskToday()}.xlsx`);

  const open = (r) => navigate(`${mpPath('ozon')}?from=${r.month}&to=${lastDay(r.month)}${accountId ? `&account=${accountId}` : ''}`);
  const cell = (v, num) => (v == null ? '—' : num ? formatNumber(v) : formatMoney(v));

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2.5 }}>
        {accounts.length > 1 && (
          <Select size="small" value={accountId || 'all'} onChange={(e) => setAccountId(e.target.value === 'all' ? '' : e.target.value)} inputProps={{ 'aria-label': 'Кабинет' }} sx={{ minWidth: 220 }}>
            <MenuItem value="all">Все кабинеты</MenuItem>
            {accounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
          </Select>
        )}
        <SubTabs value={tab} onChange={setTab} items={[{ key: 'realization', label: 'Отчёты о реализации' }, { key: 'cash', label: 'Финансовый отчёт' }]} />
        <Box sx={{ flex: 1 }} />
        {tab === 'cash' && (
          <ExportGate><Button variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={exportCash} disabled={!data?.cash_flows.length}>Скачать XLSX</Button></ExportGate>
        )}
      </Stack>

      {error && <ErrorState message={error} onRetry={load} />}
      {!data && !error && <Skeleton variant="rounded" height={360} sx={{ borderRadius: '16px' }} />}

      {data && tab === 'realization' && (data.realization.length === 0 ? (
        <Card><CardContent>
          <EmptyState title="Отчётов о реализации пока нет" description="Ozon публикует отчёт за месяц до 5-го числа следующего месяца; MARQIT загружает его автоматически." />
        </CardContent></Card>
      ) : (
        <Card><CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Отчёт о реализации товаров за месяц: доставленные и возвращённые товары (отмены и невыкупы в него не входят). Суммы — за вычетом возвратов.
            Нажмите на строку, чтобы открыть аналитику за этот месяц.
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Отчёты о реализации Ozon">
              <TableHead>
                <TableRow>
                  <TableCell>Месяц</TableCell>
                  {many && <TableCell>Кабинет</TableCell>}
                  {REAL_COLS.map((c) => <TableCell key={c.key} align="right" sx={{ whiteSpace: 'nowrap' }}>{c.label}</TableCell>)}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.realization.map((r) => (
                  <TableRow key={`${r.account_id}-${r.month}`} hover onClick={() => open(r)} sx={{ cursor: 'pointer' }}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{monthLabel(r.month)}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.number ? `№ ${r.number}` : 'продаж не было'}</Typography>
                    </TableCell>
                    {many && <TableCell>{name(r.account_id)}</TableCell>}
                    {REAL_COLS.map((c) => <TableCell key={c.key} align="right" sx={{ whiteSpace: 'nowrap' }}>{cell(r[c.key], c.num)}</TableCell>)}
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {r.rows > 0 && (
                        <ExportGate>
                          <Tooltip title="Скачать строки отчёта в XLSX">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); downloadReport(r); }} aria-label={`Скачать отчёт за ${monthLabel(r.month)}`}>
                              <FileDownloadOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </ExportGate>
                      )}
                      <ChevronRightRoundedIcon fontSize="small" sx={{ verticalAlign: 'middle', color: 'text.secondary' }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </CardContent></Card>
      ))}

      {data && tab === 'cash' && (data.cash_flows.length === 0 ? (
        <Card><CardContent>
          <EmptyState title="Финансового отчёта пока нет" description="Финансовый отчёт Ozon загружается автоматически по периодам 1–15 и 16–конец месяца." />
        </CardContent></Card>
      ) : (
        <Card><CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            «Доходы и расходы» Ozon по периодам 1–15 и 16–конец месяца, со знаками Ozon: списания — с минусом. Текущий период ещё заполняется.
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Финансовый отчёт Ozon">
              <TableHead>
                <TableRow>
                  <TableCell>Период</TableCell>
                  {many && <TableCell>Кабинет</TableCell>}
                  {CASH_COLS.map((c) => <TableCell key={c.key} align="right" sx={{ whiteSpace: 'nowrap' }}>{c.label}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.cash_flows.map((c) => (
                  <TableRow key={`${c.account_id}-${c.date_from}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(c.date_from)} — {formatDate(c.date_to)}</TableCell>
                    {many && <TableCell>{name(c.account_id)}</TableCell>}
                    {CASH_COLS.map((col) => <TableCell key={col.key} align="right" sx={{ whiteSpace: 'nowrap' }}>{cell(c[col.key])}</TableCell>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </CardContent></Card>
      ))}
    </Box>
  );
};

export default OzonReports;
